import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Copy, Check, Gift, Users, Share2 } from 'lucide-react';

import { API } from '../lib/api';

export default function InviteModal({ open, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    try {
      const saved = JSON.parse(localStorage.getItem('gynovate_signup') || 'null');
      if (!saved || !saved.phone) {
        setLoading(false);
        return;
      }
      axios.get(`${API}/api/referral/stats/${saved.phone}`)
        .then(r => setStats(r.data))
        .catch(() => toast.error('Could not load referral stats'))
        .finally(() => setLoading(false));
    } catch (e) {
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const inviteLink = stats
    ? `${window.location.origin}/signup?ref=${stats.referral_code}`
    : '';

  const whatsappMsg = `Hey 👋 — I've been using *GYNOVATE AI/SI*, an AI copilot for doctors built by Dr. Vikram Dukle. It gives evidence-based answers in seconds (drug doses, OT notes, patient communication, India-first guidelines).

Try it free (no credit card):
${inviteLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.65)' }}
      onClick={onClose}
      data-testid="invite-modal-backdrop"
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: 'linear-gradient(160deg, #1a0a0f 0%, #2d1018 55%, #1a0a0f 100%)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 text-white/70"
          data-testid="invite-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-7 pt-8 pb-6 text-center">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: 'rgba(192,18,60,0.22)' }}
          >
            <Gift className="w-7 h-7 text-raspberry-400" />
          </div>
          <h2 className="text-2xl font-black text-white mb-1.5">Invite a Colleague</h2>
          <p className="text-white/60 text-sm leading-snug">
            Help fellow gynaecologists discover<br />
            evidence-based clinical answers in seconds.
          </p>
        </div>

        {/* Stats */}
        {!loading && stats && (
          <div className="px-7 pb-5">
            <div
              className="rounded-2xl p-4 border flex items-center justify-between"
              style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.08)' }}
              data-testid="invite-stat-count"
            >
              <div className="flex items-center gap-2 text-white/60 text-sm font-medium">
                <Users className="w-4 h-4" />
                Colleagues Invited
              </div>
              <div className="text-white text-2xl font-black tracking-tight">
                {stats.referral_count}
              </div>
            </div>
          </div>
        )}

        {/* Link */}
        {!loading && stats && (
          <div className="px-7 pb-4">
            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl border"
              style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)' }}
            >
              <span className="flex-1 truncate text-xs text-white/80 font-mono" data-testid="invite-link">
                {inviteLink}
              </span>
              <button
                onClick={copyLink}
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/70"
                data-testid="invite-copy-btn"
                title="Copy link"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-white/40 mt-2 text-center">
              Your code: <span className="font-bold text-white/70 font-mono">{stats.referral_code}</span>
            </p>
          </div>
        )}

        {/* Actions */}
        {!loading && stats && (
          <div className="px-7 pb-7 space-y-2.5">
            {/* WhatsApp — form to bypass popup blockers */}
            <form
              action="https://wa.me/"
              method="GET"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <input type="hidden" name="text" value={whatsappMsg} />
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-[#25D366] text-white text-sm font-bold hover:bg-[#1fb955] transition-all flex items-center justify-center gap-2 shadow-lg"
                data-testid="invite-whatsapp-btn"
              >
                <Share2 className="w-4 h-4" />
                Share via WhatsApp
              </button>
            </form>

            <button
              onClick={copyLink}
              className="w-full py-3 rounded-xl border text-white text-sm font-bold transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.15)' }}
              data-testid="invite-copy-full-btn"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Invite Link'}
            </button>
          </div>
        )}

        {/* Invited list */}
        {!loading && stats && stats.invited_doctors && stats.invited_doctors.length > 0 && (
          <div
            className="px-7 pb-6 pt-1 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.07)' }}
          >
            <div className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3 mt-4">
              Recently Joined
            </div>
            <div className="space-y-2 max-h-36 overflow-y-auto" data-testid="invited-list">
              {stats.invited_doctors.slice(0, 5).map((d, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm">
                  <div className="w-7 h-7 rounded-full bg-raspberry-600/30 flex items-center justify-center text-raspberry-300 text-xs font-bold flex-shrink-0">
                    {(d.name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white/90 text-xs font-semibold truncate">{d.name}</div>
                    <div className="text-white/40 text-[11px] truncate">{d.designation} · {d.place_of_practice}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="px-7 pb-8 text-center text-white/50 text-sm">Loading…</div>
        )}
      </div>
    </div>
  );
}
